import { useIsAdmin } from '@sit/client-shared';
import { PRODUCTION_TENANT_TYPES, TenantTypes } from '@sit/core';
import { useGetCurrentCompany } from '@web/api/company/use-get-company';
import { BuildType, setBuildType } from '@web/redux/Development';
import { useCompanyEntities, useIsAuthenticated } from '@web/redux/hooks';
import { selectBuildType } from '@web/redux/selectors';
import { useAppDispatch, useAppSelector } from '@web/redux/store';
import omit from 'lodash/omit';
import { useLocation, useParams } from 'react-router';
import { handleParamsValidation } from '../helpers';

type PrivatePageStates = 'UNAUTHENTICATED' | 'INVALID_PARAMS' | 'UNPROVISIONED' | 'AUTHORIZED';

interface UsePrivateDataProps {
  paramsToValidate?: Record<string, string>;
}

const NON_PRODUCTION_TENANTS = Object.values(omit(TenantTypes, PRODUCTION_TENANT_TYPES));

export const usePrivateData = ({ paramsToValidate }: UsePrivateDataProps): PrivatePageStates => {
  const dispatch = useAppDispatch();
  const companyEntities = useCompanyEntities();
  const { data: company } = useGetCurrentCompany();
  const isAuthenticated = useIsAuthenticated();
  const isAdmin = useIsAdmin({ enabled: isAuthenticated });
  const buildType = useAppSelector(selectBuildType);
  const params = useParams();
  const location = useLocation();
  const shouldValidateParams = paramsToValidate && Object.keys(paramsToValidate).length > 0;
  const paramsAreValid = shouldValidateParams && handleParamsValidation(paramsToValidate, params);
  const { pathname, search } = location;

  if (!isAuthenticated) {
    return 'UNAUTHENTICATED';
  }

  if (shouldValidateParams && !paramsAreValid) {
    return 'INVALID_PARAMS';
  }

  if (companyEntities && isAdmin && sessionStorage.getItem('visited-advanced') !== 'true' && pathname !== '/time-preferences/advanced') {
    sessionStorage.setItem('visited-advanced', 'true');
    return 'UNPROVISIONED';
  }
  const searchParams = new URLSearchParams(search);
  const queryForceEmbedded = searchParams.get('force_embedded_mode') === 'true';

  if (
    buildType !== BuildType.embedded &&
    queryForceEmbedded &&
    company?.tenantType &&
    (NON_PRODUCTION_TENANTS as string[]).includes(company.tenantType)
  ) {
    dispatch(setBuildType(BuildType.embedded));
  }

  return 'AUTHORIZED';
};
